import { apiFetch } from '../lib/apiFetch';

// ============================================
// WIDGET TELEMETRY API CLIENT
// ============================================
// Mirrors `marketing.ts`. All endpoints are authenticated and support an
// optional set of filters (ipRegionCode, utmSource, deviceCategory) that the
// backend applies as GA4 dimension filters on the booking-widget property
// (530668124). Date range defaults to the last 30 days server-side if omitted.
//
// Types here mirror the backend's widget-telemetry.service.ts exactly. Keep
// them in sync when the service changes.

// ── Shared types ──

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface WidgetTelemetryFilters {
  ipRegionCode?: string;
  utmSource?: string;
  deviceCategory?: 'mobile' | 'desktop' | 'tablet';
}

// ── Funnel ──

export type FunnelStage =
  | 'widget_loaded'
  | 'geolocation_resolved'
  | 'venue_viewed'
  | 'workshop_selected'
  | 'begin_checkout'
  | 'purchase';

export interface FunnelRow {
  stage: FunnelStage;
  sessions: number;
  /** Sessions at this stage / sessions at widget_loaded (0..1). */
  cumulativeRate: number;
  /** Sessions at this stage / sessions at previous stage (0..1). */
  stepRate: number;
}

export interface FunnelResult {
  stages: FunnelRow[];
  totalSessions: number;
  dateRange: DateRange;
}

// ── Pain points ──

export interface TopErrorRow {
  message: string;
  source: string;
  count: number;
}

export interface TopZeroResultRow {
  locationLabel: string;
  count: number;
}

export interface PainPointsResult {
  zeroResultRate: number;
  geolocationFailureRate: number;
  browseAbandonmentRate: number;
  errorRate: number;
  topErrors: TopErrorRow[];
  topZeroResultLocations: TopZeroResultRow[];
  dateRange: DateRange;
}

// ── Browse behavior ──

export interface TopWorkshopTypeRow {
  workshopType: string;
  impressions: number;
  clicks: number;
  /** Clicks / impressions (0..1). */
  ctr: number;
}

export interface BrowseBehaviorResult {
  impressions: {
    totalImpressions: number;
    totalClicks: number;
    sessionsWithImpressions: number;
    sessionsWithClicks: number;
    clickThroughRate: number;
  };
  filterEngagement: {
    sessionsApplyingFilters: number;
    totalFilterApplications: number;
    totalFilterClears: number;
    filterToSelectRate: number;
  };
  searchBehavior: {
    sessionsWithLocationChange: number;
    totalLocationChanges: number;
    retrySearches: number;
  };
  engagement: {
    sessionsUsingLoadMore: number;
    sessionsSwitchingView: number;
  };
  topWorkshopTypes: TopWorkshopTypeRow[];
  dateRange: DateRange;
}

// ── Geo cross-reference ──

export interface GeoWidgetRow {
  ipRegionCode: string;
  sessionsWidgetLoaded: number;
  sessionsWorkshopSelected: number;
  sessionsPurchase: number;
  conversionRate: number;
}

export interface GeoWarehouseRow {
  bonsaibarRegion: string;
  regionKey: number | null;
  totalBookings: number;
  bookingRevenue: number;
  avgBookingValue: number;
}

export interface GeoCrossRefResult {
  widgetByState: GeoWidgetRow[];
  bookingsByRegion: GeoWarehouseRow[];
  dateRange: DateRange;
}

// ── Revenue overview ──

export interface RevenueSummary {
  totalBookings: number;
  totalRevenue: number;
  averageBookingValue: number;
  /** GA4 widget_loaded session count for the same window. `null` when GA4 is unavailable. */
  widgetSessions: number | null;
  /** totalBookings / widgetSessions, 0..1. `null` when widgetSessions is null. */
  conversionRate: number | null;
}

export interface RevenueByDimensionRow {
  key: string;
  name: string;
  bookings: number;
  revenue: number;
}

export interface RevenueOverTimePoint {
  date: string;
  bookings: number;
  revenue: number;
}

export interface RevenueOverviewResult {
  summary: RevenueSummary;
  byVenue: RevenueByDimensionRow[];
  byRegion: RevenueByDimensionRow[];
  overTime: RevenueOverTimePoint[];
  dateRange: DateRange;
  notes: string[];
}

// ── Performance ──

export type PerformanceEvent =
  | 'widget_time_to_interactive'
  | 'location_resolve_time'
  | 'venue_detail_load_time';

export interface PerformanceRow {
  event: PerformanceEvent;
  p50: number;
  p75: number;
  p95: number;
  sampleCount: number;
}

export interface PerformanceResult {
  events: PerformanceRow[];
  dateRange: DateRange;
}

// ── Query builder ──

function buildQuery(
  range: DateRange | undefined,
  filters: WidgetTelemetryFilters | undefined,
): string {
  const q = new URLSearchParams();
  if (range?.startDate) q.set('startDate', range.startDate);
  if (range?.endDate) q.set('endDate', range.endDate);
  if (filters?.ipRegionCode) q.set('ipRegionCode', filters.ipRegionCode);
  if (filters?.utmSource) q.set('utmSource', filters.utmSource);
  if (filters?.deviceCategory) q.set('deviceCategory', filters.deviceCategory);
  return q.toString();
}

// ── Fetchers ──

export async function fetchFunnel(
  range?: DateRange,
  filters?: WidgetTelemetryFilters,
): Promise<FunnelResult & { filters: WidgetTelemetryFilters }> {
  const query = buildQuery(range, filters);
  const res = await apiFetch(`/api/v1/widget-telemetry/funnel?${query}`);
  if (!res.ok) throw new Error('Failed to fetch widget funnel');
  return res.json();
}

export async function fetchPainPoints(
  range?: DateRange,
  filters?: WidgetTelemetryFilters,
): Promise<PainPointsResult & { filters: WidgetTelemetryFilters }> {
  const query = buildQuery(range, filters);
  const res = await apiFetch(`/api/v1/widget-telemetry/pain-points?${query}`);
  if (!res.ok) throw new Error('Failed to fetch widget pain points');
  return res.json();
}

export async function fetchPerformance(
  range?: DateRange,
  filters?: WidgetTelemetryFilters,
): Promise<PerformanceResult & { filters: WidgetTelemetryFilters }> {
  const query = buildQuery(range, filters);
  const res = await apiFetch(`/api/v1/widget-telemetry/performance?${query}`);
  if (!res.ok) throw new Error('Failed to fetch widget performance');
  return res.json();
}

export async function fetchBrowseBehavior(
  range?: DateRange,
  filters?: WidgetTelemetryFilters,
): Promise<BrowseBehaviorResult & { filters: WidgetTelemetryFilters }> {
  const query = buildQuery(range, filters);
  const res = await apiFetch(`/api/v1/widget-telemetry/browse-behavior?${query}`);
  if (!res.ok) throw new Error('Failed to fetch widget browse behavior');
  return res.json();
}

export async function fetchGeoCrossRef(
  range?: DateRange,
  filters?: WidgetTelemetryFilters,
): Promise<GeoCrossRefResult & { filters: WidgetTelemetryFilters }> {
  const query = buildQuery(range, filters);
  const res = await apiFetch(`/api/v1/widget-telemetry/geo?${query}`);
  if (!res.ok) throw new Error('Failed to fetch widget geo cross-reference');
  return res.json();
}

export async function fetchRevenueOverview(
  range?: DateRange,
  filters?: WidgetTelemetryFilters,
): Promise<RevenueOverviewResult & { filters: WidgetTelemetryFilters }> {
  const query = buildQuery(range, filters);
  const res = await apiFetch(`/api/v1/widget-telemetry/revenue-overview?${query}`);
  if (!res.ok) throw new Error('Failed to fetch widget revenue overview');
  return res.json();
}
